import {
  getAddressDetail,
  getAddressList,
  GetAddressListType,
} from '@/api/newApi/personalCenter/address';
import {
  getAddressId,
  getStoredSelectedAddress,
  pickAddressByPriority,
} from '@/util/addressSelection';

export const HEADER_ADDRESS_LIST_PARAMS: GetAddressListType = {
  pageNo: 1,
  pageSize: 1000,
  keywords: '',
};

export type HeaderAddressRefreshOptions = {
  preferredAddressId?: number | string;
  submittedAddress?: any;
  forceRefresh?: boolean;
};

export type HeaderAddressRefreshResult = {
  list: any[];
  selected: any | null;
  selectedSource: 'manual' | 'default';
};

export function normalizeAddressListItem(item: any) {
  if (!item) {
    return null;
  }

  return {
    ...item,
    id: item.addressManagerId || item.addressId || item.id,
    addressId: item.addressManagerId || item.addressId || item.id,
    receiveName: item.receiverName || item.receiveName,
    receivePhone: item.receiverPhone || item.receivePhone,
    detailAddress: item.detailAddress,
    areaAddress:
      item.areaAddress ||
      [item.countryName, item.provinceName, item.cityName, item.countyName].filter(Boolean).join(' '),
    postalCode: item.postalCode || '',
    defaultStatus: item.defaultStatus || 0,
    isSelf: item.isSelf,
    creator: item.creator,
    pin: item.pin,
    createPin: item.createPin,
    country: item.country,
    countryName: item.countryName,
    provinceName: item.provinceName,
    cityName: item.cityName,
    countyName: item.countyName,
    sortRule: item.sortRule,
  };
}

export function normalizeAddressListItems(items: any[] = []) {
  return items.map(normalizeAddressListItem).filter(Boolean) as any[];
}

/** 头部/SKU/结算等共用的地址列表：走 API 层缓存 */
export async function fetchNormalizedHeaderAddressList(forceRefresh = false) {
  const addrRes: any = await getAddressList(HEADER_ADDRESS_LIST_PARAMS, forceRefresh);
  if (!addrRes?.success || !addrRes?.value) {
    return [];
  }
  return normalizeAddressListItems(addrRes.value.items || []);
}

export async function fetchAddressDetailMerged(address: any, forceRefresh = false) {
  const addressId = getAddressId(address);
  if (!addressId) {
    return address;
  }

  try {
    const detailRes: any = await getAddressDetail(addressId, forceRefresh);
    if (detailRes?.success && detailRes?.value) {
      return {
        ...address,
        ...detailRes.value,
        id: detailRes.value.addressId || addressId,
      };
    }
  } catch (error) {
    console.error('fetchAddressDetailMerged failed', error);
  }

  return address;
}

/**
 * 头部地址刷新：list + 选中地址 detail 合并。
 * 会话级默认刷新由 sessionBootstrap 保证只跑一次；此处依赖 address API 层缓存兜底并发。
 */
export async function refreshHeaderAddress(
  options: HeaderAddressRefreshOptions = {},
): Promise<HeaderAddressRefreshResult> {
  const { preferredAddressId, submittedAddress, forceRefresh = false } = options;
  const shouldForceRefresh = forceRefresh || Boolean(preferredAddressId || submittedAddress);

  const list = await fetchNormalizedHeaderAddressList(shouldForceRefresh);
  if (!list.length) {
    return { list: [], selected: null, selectedSource: 'default' };
  }

  const stored = getStoredSelectedAddress();
  let nextSelected = pickAddressByPriority(list, {
    preferredAddressId,
    submittedAddress,
    storedAddress: stored,
  });

  if (!getAddressId(nextSelected)) {
    return { list, selected: null, selectedSource: 'default' };
  }

  nextSelected = await fetchAddressDetailMerged(nextSelected, shouldForceRefresh);

  const selectedSource: 'manual' | 'default' =
    preferredAddressId || submittedAddress || stored?.addressId ? 'manual' : 'default';

  return {
    list,
    selected: nextSelected,
    selectedSource,
  };
}
